/**
 * 트랙 액션 훅
 *
 * 트랙 관련 모든 mutation을 통합 관리합니다.
 * - 트랙 수정
 * - 트랙 삭제
 * - 트랙 재생성
 */

'use client';

import { useQueryClient } from '@tanstack/react-query';
import { invalidateTrackWithPlaylist, invalidateTrack, invalidateTrackList, invalidatePlaylist, invalidatePlaylistTracks } from '@/lib/api/cacheUtils';
import {
  useTrackPartialUpdate,
  useTrackDestroy,
  useTrackRegenerate,
} from '@/lib/api/orval';
import { useTrackGenerateMusic, useTrackResetStuck } from '@/lib/api/orval/endpoints/tracks/tracks';
import { useToast } from '@/lib/hooks/useToast';
import { createMutationErrorHandler } from '@/lib/utils/errorUtils';
import type { RegenerateTrackFormData } from '@/lib/validations';

// ============================================================================
// Types
// ============================================================================

export interface UseTrackActionsOptions {
  playlistId: string;
  trackId: string | undefined;
  onEditSuccess?: () => void;
  onDeleteSuccess?: () => void;
  onRegenerateSuccess?: () => void;
  onGenerateMusicSuccess?: () => void;
  onResetStuckSuccess?: () => void;
}

// ============================================================================
// Hook
// ============================================================================

export function useTrackActions({
  playlistId,
  trackId,
  onEditSuccess,
  onDeleteSuccess,
  onRegenerateSuccess,
  onGenerateMusicSuccess,
  onResetStuckSuccess,
}: UseTrackActionsOptions) {
  const queryClient = useQueryClient();
  const { success: toastSuccess, error: toastError } = useToast();

  // 트랙 수정
  const updateTrack = useTrackPartialUpdate({
    mutation: {
      onSuccess: () => {
        invalidateTrackWithPlaylist(queryClient, playlistId, trackId);
        toastSuccess('트랙이 수정되었습니다');
        onEditSuccess?.();
      },
      onError: createMutationErrorHandler(toastError, '수정 실패'),
    },
  });

  // 트랙 삭제
  const deleteTrack = useTrackDestroy({
    mutation: {
      onSuccess: () => {
        invalidateTrackList(queryClient);
        invalidatePlaylist(queryClient, playlistId);
        // 플레이리스트 트랙 목록도 무효화 (삭제 시 UI 즉시 반영을 위해)
        invalidatePlaylistTracks(queryClient, playlistId);
        toastSuccess('트랙이 삭제되었습니다');
        onDeleteSuccess?.();
      },
      onError: createMutationErrorHandler(toastError, '삭제 실패'),
    },
  });

  // 트랙 재생성
  const regenerateTrack = useTrackRegenerate({
    mutation: {
      onSuccess: () => {
        if (trackId) {
          invalidateTrack(queryClient, trackId);
        }
        invalidatePlaylist(queryClient, playlistId);
        // 플레이리스트 트랙 목록도 무효화 (재생성 시 UI 즉시 반영을 위해)
        invalidatePlaylistTracks(queryClient, playlistId);
        toastSuccess('트랙 재생성을 시작했습니다');
        onRegenerateSuccess?.();
      },
      onError: createMutationErrorHandler(toastError, '트랙 재생성 실패'),
    },
  });

  // 트랙 음악 생성 (metadata_ready → generating)
  const generateMusic = useTrackGenerateMusic({
    mutation: {
      onSuccess: () => {
        if (trackId) {
          invalidateTrack(queryClient, trackId);
        }
        invalidatePlaylist(queryClient, playlistId);
        // 플레이리스트 트랙 목록도 무효화 (음악 생성 시 UI 즉시 반영을 위해)
        invalidatePlaylistTracks(queryClient, playlistId);
        toastSuccess('음악 생성을 시작했습니다');
        onGenerateMusicSuccess?.();
      },
      onError: createMutationErrorHandler(toastError, '음악 생성 시작 실패'),
    },
  });

  // 스턱 트랙 리셋 (music_generating → failed)
  const resetStuck = useTrackResetStuck({
    mutation: {
      onSuccess: () => {
        if (trackId) {
          invalidateTrack(queryClient, trackId);
        }
        invalidatePlaylist(queryClient, playlistId);
        invalidatePlaylistTracks(queryClient, playlistId);
        toastSuccess('트랙이 리셋되었습니다. 재시도할 수 있습니다.');
        onResetStuckSuccess?.();
      },
      onError: createMutationErrorHandler(toastError, '트랙 리셋 실패'),
    },
  });

  // 핸들러
  const handleEdit = async (data: { title: string; lyrics: string }) => {
    if (!trackId) { return; }
    await updateTrack.mutateAsync({ id: trackId, data });
  };

  // Note: musicPrompt 필드 삭제됨 - suno_style이 실제 스타일 태그로 사용됨
  const handleEditPrompts = async (data: { imagePrompt?: string; sunoStyle?: string; videoPrompt?: string }) => {
    if (!trackId) { return; }
    await updateTrack.mutateAsync({ id: trackId, data });
  };

  const handleDelete = async (id: string) => {
    await deleteTrack.mutateAsync({ id });
  };

  const handleRegenerate = async () => {
    if (!trackId) { return; }
    await regenerateTrack.mutateAsync({ id: trackId });
  };

  // 프롬프트 수정 후 재생성 (트랙 업데이트 → 재생성)
  const handleRegenerateWithPrompts = async (data: RegenerateTrackFormData) => {
    if (!trackId) { return; }

    // 1. 트랙 필드 업데이트 (변경된 필드만)
    const updateData: Record<string, string | undefined> = {};
    if (data.title !== undefined && data.title !== '') {
      updateData.title = data.title;
    }
    if (data.lyrics !== undefined && data.lyrics !== '') {
      updateData.lyrics = data.lyrics;
    }
    if (data.imagePrompt !== undefined && data.imagePrompt !== '') {
      updateData.imagePrompt = data.imagePrompt;
    }
    if (data.sunoStyle !== undefined && data.sunoStyle !== '') {
      updateData.sunoStyle = data.sunoStyle;
    }

    // 업데이트할 내용이 있으면 먼저 트랙 업데이트
    if (Object.keys(updateData).length > 0) {
      await updateTrack.mutateAsync({
        id: trackId,
        data: updateData,
      });
    }

    // 2. 재생성 시작
    await regenerateTrack.mutateAsync({ id: trackId });
  };

  const handleGenerateMusic = async (id?: string) => {
    const targetId = id || trackId;
    if (!targetId) { return; }
    await generateMusic.mutateAsync({ id: targetId });
  };

  const handleResetStuck = async (id?: string) => {
    const targetId = id || trackId;
    if (!targetId) { return; }
    await resetStuck.mutateAsync({ id: targetId });
  };

  return {
    // 로딩 상태
    isUpdating: updateTrack.isPending,
    isDeleting: deleteTrack.isPending,
    isRegenerating: regenerateTrack.isPending,
    isGeneratingMusic: generateMusic.isPending,
    isResettingStuck: resetStuck.isPending,

    // 핸들러
    handleEdit,
    handleEditPrompts,
    handleDelete,
    handleRegenerate,
    handleRegenerateWithPrompts,
    handleGenerateMusic,
    handleResetStuck,
  };
}

export type UseTrackActionsReturn = ReturnType<typeof useTrackActions>;
